import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, PageHeader } from '@/components/ui/primitives';
import { EngineDashboard } from '@/components/EngineDashboard';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { CreateDialog } from '@/components/CreateDialog';
import {
  captureSeoCompetitorSnapshot,
  fetchSeoCompetitors,
  fetchSeoProjectOptions,
  type SeoCompetitorRow,
} from '@/data/sources';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { formatRelative } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/competitors — stored competitor snapshots per project
 * (api/seo.yaml: GET/POST /seo/projects/{id}/competitors). Evidence-first
 * honesty: each snapshot is the provider overview document exactly as captured
 * (DataForSEO Labs), rendered newest first with its source attribution and the
 * raw payload one click away — nothing is reshaped or invented. Captures are
 * paid + budget-guarded server-side: rows are priced up-front, and 402
 * budget_exceeded / 502 provider_error / 503 provider_unavailable all render
 * as honest human guidance, never raw errors. */

interface InlinePanel {
  title: string;
  body: string;
  link?: { to: string; label: string };
}

/** Row count of the captured overview, if the payload carries one — else null
 *  (rendered as an honest em-dash, never a guessed number). */
function snapshotRowCount(snapshot: Record<string, unknown>): number | null {
  if (Array.isArray(snapshot.items)) return snapshot.items.length;
  return null;
}

function SnapshotCard({ row }: { row: SeoCompetitorRow }) {
  const rows = snapshotRowCount(row.snapshot);
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-text-primary">{row.competitor_domain}</span>
        {rows !== null && (
          <span className="text-xs text-text-secondary" data-tabular>
            {rows} row{rows === 1 ? '' : 's'} captured
          </span>
        )}
        <span className="ml-auto text-xs text-text-tertiary">
          {formatRelative(row.captured_at)} · source: {row.source}
        </span>
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-text-tertiary hover:text-text-secondary">
          View captured overview
        </summary>
        <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-inset p-3 text-[11px] leading-relaxed text-text-secondary">
          {JSON.stringify(row.snapshot, null, 2)}
        </pre>
      </details>
    </Card>
  );
}

export function SeoCompetitorsPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [panel, setPanel] = useState<InlinePanel | null>(null);

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();
  const projectId = resolveActiveProjectId(active, projects);
  const project = projects.find((p) => p.id === projectId) ?? null;

  const competitorsQ = useQuery({
    queryKey: ['seo', 'competitors', projectId],
    queryFn: () => fetchSeoCompetitors(projectId as string),
    enabled: projectId !== null,
  });

  const handleCapture = async (values: Record<string, string>) => {
    if (projectId === null) throw new Error('Select a project first.');
    const domain = (values.competitor_domain ?? '').trim();
    if (domain === '') throw new Error('Enter the competitor’s domain.');
    if (domain.length > 255) throw new Error('The domain must be 255 characters or fewer.');
    const limitRaw = (values.limit ?? '').trim();
    let limit: number | undefined;
    if (limitRaw !== '') {
      limit = Number(limitRaw);
      if (!Number.isInteger(limit) || limit < 1 || limit > 100) {
        throw new Error('Rows must be a whole number between 1 and 100 (leave blank for 10).');
      }
    }
    try {
      const res = await captureSeoCompetitorSnapshot(projectId, {
        competitor_domain: domain,
        limit,
      });
      setDialogOpen(false);
      setPanel(null);
      setNotice(
        `Snapshot of ${domain} captured — ${res.items} row${res.items === 1 ? '' : 's'} stored (source: ${res.source}).`,
      );
      await queryClient.invalidateQueries({ queryKey: ['seo', 'competitors', projectId] });
    } catch (err) {
      // Provider/budget outcomes close the dialog and render as the house
      // honest panels; validation stays inline in the dialog.
      if (err instanceof ApiError && (err.status === 402 || err.code === 'budget_exceeded')) {
        setDialogOpen(false);
        setPanel({
          title: 'Monthly provider budget reached',
          body: 'This snapshot would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. Captures resume when the budget resets next month.',
          link: { to: '/seo/spend', label: 'Review spend & budget →' },
        });
        return;
      }
      if (err instanceof ApiError && (err.status === 502 || err.code === 'provider_error')) {
        setDialogOpen(false);
        setPanel({
          title: 'The provider request failed',
          body: `${err.message ? `${err.message} ` : ''}Nothing was stored — previously captured snapshots below are unaffected. Try again in a moment.`,
        });
        return;
      }
      if (err instanceof ApiError && (err.status === 503 || err.code === 'provider_unavailable')) {
        setDialogOpen(false);
        setPanel({
          title: 'Competitor snapshots aren’t available yet',
          body: 'Snapshots need the data provider connection (DataForSEO), which isn’t configured for this environment yet. Nothing was charged — previously captured snapshots still show below.',
        });
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        throw new Error(err.message || 'Please check the domain and try again.');
      }
      throw err instanceof Error
        ? err
        : new Error('The snapshot could not be captured. Please try again.');
    }
  };

  const captureDialog = canManage ? (
    <CreateDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      title="Capture competitor snapshot"
      description={
        project
          ? `Capture a competitor overview for ${project.label} from DataForSEO Labs. Rows are priced up-front and charged against the monthly provider budget before the call is made.`
          : undefined
      }
      submitLabel="Capture snapshot"
      onSubmit={handleCapture}
      fields={[
        {
          name: 'competitor_domain',
          label: 'Competitor domain',
          required: true,
          placeholder: 'e.g. fresha.com',
          hint: 'The competitor site to snapshot, as a bare domain (max 255 characters).',
        },
        {
          name: 'limit',
          label: 'Rows',
          placeholder: '10',
          hint: 'Max competitor rows to capture, 1–100. Rows are priced up-front — leave blank for 10.',
        },
      ]}
    />
  ) : null;

  const captureButton =
    canManage && projects.length > 0 ? (
      <Button variant="primary" onClick={() => setDialogOpen(true)}>
        ＋ Capture snapshot
      </Button>
    ) : null;

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Competitors" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Competitors" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }

  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Competitors" />
        <EmptyState
          title="No domains to compare against yet"
          teach="Competitor snapshots live inside an SEO project. Add your first domain on the Projects page, then come back here to capture who you’re really up against."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const snapshots = competitorsQ.data ?? [];

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => {
          void select(Number(e.target.value));
          setNotice(null);
          setPanel(null);
        }}
        className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Competitors"
        actions={
          <>
            {projectSelector}
            {captureButton}
          </>
        }
      />

      {notice && (
        <div className="rounded-md border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
          {notice}
        </div>
      )}

      {panel && (
        <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
          <div className="text-sm font-medium text-text-primary">{panel.title}</div>
          <p className="mt-1 text-sm text-text-secondary">{panel.body}</p>
          {panel.link && (
            <Link
              to={panel.link.to}
              className="mt-2 inline-block text-sm font-medium text-engine hover:underline"
            >
              {panel.link.label}
            </Link>
          )}
        </div>
      )}

      <EngineDashboard
        engineName="SEO Engine"
        heroLabel={project ? `Competitor snapshots — ${project.label}` : 'Competitor snapshots'}
        hero={<span data-tabular>{competitorsQ.isSuccess ? snapshots.length : '—'}</span>}
        heroSub="Point-in-time competitor overviews from DataForSEO Labs, stored exactly as captured and always source-attributed — never a proprietary-index claim."
      >
        {competitorsQ.isPending ? (
          <LoadingState rows={4} />
        ) : competitorsQ.error ? (
          <ErrorState error={competitorsQ.error} onRetry={() => void competitorsQ.refetch()} />
        ) : snapshots.length === 0 ? (
          <EmptyState
            title="No competitor snapshots yet"
            teach={`Capture a snapshot of who ${project?.domain ?? 'this domain'} competes with in search — each capture is run on demand, source-attributed, and priced up-front against your monthly provider budget.`}
            action={
              canManage ? (
                <Button variant="primary" onClick={() => setDialogOpen(true)}>
                  ＋ Capture snapshot
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-3">
            {snapshots.map((s) => (
              <SnapshotCard key={s.id} row={s} />
            ))}
          </div>
        )}
      </EngineDashboard>

      {captureDialog}
    </div>
  );
}
